import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";

const Leave = () => {
  const [leaveType, setLeaveType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Ensure navigate is declared inside the component
  const navigate = useNavigate();

  // Fetch the leave history for the logged in user
  const fetchLeaves = async () => {
    try {
      const response = await api.get("/leave/leavelist");
      setLeaves(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching leave records.");
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const handleApplyLeave = async () => {
    // Ensure all fields are filled
    if (!leaveType || !fromDate || !toDate || !reason) {
      setError("All fields are required.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      // Payload now includes the "reason" field
      await api.post("/leave/request", {
        leave_type: leaveType,
        start_date: fromDate,
        end_date: toDate,
        reason: reason
      });
      setSuccess("Leave applied successfully.");
      setLeaveType("");
      setFromDate("");
      setToDate("");
      setReason("");
      fetchLeaves();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to apply leave.");
    }
    setLoading(false);
  };

  // Fallback back function in case there's no history
  const handleBack = () => {
      navigate("/dashboard"); // default route if no history exists
    }
  

  return (
    <Container maxWidth="sm">
      <Paper elevation={4} sx={{ p: 4, my: 4, borderRadius: 2 }}>
        {/* Back button */}
        <Typography variant="h5" gutterBottom>
        <IconButton onClick={handleBack} sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        
        
          Leave Application
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="leave-type-label">Leave Type</InputLabel>
            <Select
              labelId="leave-type-label"
              value={leaveType}
              label="Leave Type"
              onChange={(e) => setLeaveType(e.target.value)}
            >
              <MenuItem value="Sick">Sick Leave</MenuItem>
              <MenuItem value="CasualLeave">Casual Leave</MenuItem>
              <MenuItem value="EarnedLeave">Earned Leave</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            label="From Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          
          <TextField
            label="To Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          
          <TextField
            label="Reason"
            multiline
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          <Button variant="contained" color="primary" onClick={handleApplyLeave} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Apply Leave"}
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Leave History
          </Typography>
          {leaves.length === 0 ? (
            <Typography>No leave records found.</Typography>
          ) : (
            leaves.map((leave, index) => (
              <Paper key={index} sx={{ p: 2, mb: 1 }}>
                <Typography>
                  <strong>Type:</strong> {leave.leave_type}
                </Typography>
                <Typography>
                  <strong>Duration:</strong> {leave.start_date} to {leave.end_date}
                </Typography>
                {leave.status && (
                  <Typography>
                    <strong>Status:</strong> {leave.status}
                  </Typography>
                )}
                {leave.reason && (
                  <Typography>
                    <strong>Reason:</strong> {leave.reason}
                  </Typography>
                )}
                <Typography>
                  <strong>Days:</strong> {leave.days}
                </Typography>
                <Typography>
                  <strong>Date:</strong> {leave.date}
                </Typography>
              </Paper>
            ))
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Leave;
